// UserProfileForm.jsx
import React, {useState} from 'react';
import { router, usePage, useForm } from '@inertiajs/react';
import { User, Eye, Save, Plus, Trash2, MapPin, Clock, Tag } from 'lucide-react';
import { toast } from 'react-toastify';
import { debounce } from 'lodash';
import SocialLinksField from './SocialLinksField';

const UserProfileForm = ({ onPublicChange, onNicknameChange })  => {
  const { profile, writerTypes, experienceLevels, availabilityOptions, urls } = usePage().props;
const socialPlatforms = {
  twitter: { label: 'Twitter/X', prefix: 'https://x.com/' },
  linkedin: { label: 'LinkedIn', prefix: 'https://linkedin.com/in/' },
  instagram: { label: 'Instagram', prefix: 'https://instagram.com/' },
  youtube: { label: 'YouTube', prefix: 'https://youtube.com/' },
  personal: { label: 'Personal Website', prefix: 'https://' },
};
  
  // Profile form
  const { data, setData, post, processing, errors } = useForm({
    nickname: profile.nickname || '',
    display_name: profile.display_name || '', 
    bio: profile.bio || '',
    writer_type: profile.writer_type || '',
    experience_level: profile.experience_level || '',
    social_links: profile.social_links || [],
    is_public: profile.is_public || false,
    allow_messages: profile.allow_messages || true,
    receive_feedback_notifications: profile.receive_feedback_notifications || true,
    profile_photo: null,
    // New contact fields
    location: profile.location || '',
    availability_status: profile.availability_status || 'available',
    primary_genres: profile.primary_genres || [],
    specialty_tagline: profile.specialty_tagline || '',
  });

const [nicknameAvailable, setNicknameAvailable] = useState(true);
const [checkingNickname, setCheckingNickname] = useState(false);
const [profileImagePreview, setProfileImagePreview] = useState(profile.profile_photo_url);

// Add this function
const checkNicknameAvailability = debounce(async (nickname) => {
  if (!nickname) {
    setNicknameAvailable(true);
    return;
  }
  
  setCheckingNickname(true);
  
  try {
    const response = await fetch(`/check-nickname/${nickname}`);
    const result = await response.json();
    setNicknameAvailable(result.available);
  } catch (error) {
    console.error('Error checking nickname:', error);
  } finally {
    setCheckingNickname(false);
  }
}, 500);

// Modify your onChange handler for nickname
const handleNicknameChange = (e) => {
  const value = e.target.value;
  setData('nickname', value);
  checkNicknameAvailability(value);
};

  const handleSubmit = (e) => {
    e.preventDefault();
post(urls.update, {
  preserveScroll: true,
  preserveState: true,
  onSuccess: () => {
    setData('profile_photo', null);
    URL.revokeObjectURL(profileImagePreview);
    toast.success('Profile saved!');
  if (onPublicChange) onPublicChange(data.is_public);
  if (onNicknameChange) onNicknameChange(data.nickname);
  router.reload({ only: ['profile'] });
  },
  onError: () => {
    toast.error('There was a problem saving your profile.');
  },
});
  };
  
  const handleProfilePhotoChange = (e) => {
    setData('profile_photo', e.target.files[0]);
    const tempUrl = URL.createObjectURL(e.target.files[0]);
    setProfileImagePreview(tempUrl);
  };

  // Handle primary genres
  const addGenre = (genre) => {
    if (!genre.trim()) return;
    if (data.primary_genres.length >= 3) {
      toast.warn('Maximum 3 primary genres allowed');
      return;
    }
    if (data.primary_genres.includes(genre.trim())) {
      toast.warn('Genre already added');
      return;
    }
    setData('primary_genres', [...data.primary_genres, genre.trim()]);
  };

  const removeGenre = (indexToRemove) => {
    setData('primary_genres', data.primary_genres.filter((_, index) => index !== indexToRemove));
  };
  
  return (
    <form onSubmit={handleSubmit} className="card">
      <div className="card-header bg-light">
        <h5 className="mb-0">
          <User className="me-2" size={20} /> Basic Information
        </h5>
      </div>
      
      {/* Profile Visibility Toggle */}
      <div className="alert alert-warning m-3">
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            <div className="me-3 p-2 bg-warning bg-opacity-25 rounded-circle">
              <Eye size={18} className="text-warning" />
            </div>
            <div>
              <h5 className="mb-0 fw-bold">Profile Visibility</h5>
              <p className="mb-0 text-muted small">
                Control whether your profile is visible to other users
              </p>
            </div>
          </div>
          <div className="form-check form-switch">
            <input 
              type="checkbox" 
              className="form-check-input" 
              id="profileVisibility"
              checked={data.is_public}
              onChange={() => setData('is_public', !data.is_public)}
            />
            <label className="form-check-label" htmlFor="profileVisibility">
              {data.is_public ? 'Public' : 'Private'}
            </label>
          </div>
        </div>
      </div>
      
      <div className="card-body">
        <div className="row g-3">
          {/* Profile Photo */}
          <div className="col-md-6">
            <label className="form-label">Profile Photo</label>
            <div className="d-flex align-items-center">
              <div className="me-3" style={{ width: '64px', height: '64px' }}>
                <div className="rounded-circle bg-light d-flex align-items-center justify-content-center" style={{ width: '100%', height: '100%', overflow: 'hidden' }}>
                  {profile.profile_photo_url ? (
<img 
  src={profileImagePreview} 
  alt="Profile" 
  className="w-100 h-100 object-fit-cover"
/>
                  ) : (
                    <User size={24} className="text-secondary" />
                  )}
                </div>
              </div>
              <div>
                <label className="btn btn-sm btn-outline-secondary">
                  Upload Photo
                  <input
                    type="file"
                    className="d-none"
                    onChange={handleProfilePhotoChange}
                    accept="image/*"
                  />
                </label>
                {data.profile_photo && (
                  <span className="ms-2 text-success small">
                    New photo selected
                  </span>
                )}
              </div>
            </div>
            {errors.profile_photo && (
              <div className="text-danger small mt-1">{errors.profile_photo}</div>
            )}
          </div>

{/* Display Name Field */}
<div className="col-md-6">
  <label htmlFor="display_name" className="form-label">Display Name</label>
  <input
    type="text"
    id="display_name"
    className={`form-control ${errors.display_name ? 'is-invalid' : ''}`}
    value={data.display_name || ''}
    onChange={(e) => setData('display_name', e.target.value)}
    placeholder="Your name as it will appear publicly"
  />
  {errors.display_name && (
    <div className="invalid-feedback d-block">{errors.display_name}</div>
  )}
  <small className="text-muted">This will appear at the top of your public profile.</small>
</div>

{/* Username/Nickname Field */}
<div className="col-md-6">
  <label htmlFor="nickname" className="form-label">Username</label>
  <div className="input-group">
    <span className="input-group-text">scriptreader.ai/portfolios/</span>
    <input
      type="text"
      id="nickname"
      className={`form-control ${errors.nickname ? 'is-invalid' : ''}`}
      placeholder="your-username"
      value={data.nickname || ''}
      onChange={handleNicknameChange}
    />
            {checkingNickname && (
                <span className="input-group-text">
                  <div className="spinner-border spinner-border-sm" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </span>
              )}
              {!checkingNickname && data.nickname && (
                <span className="input-group-text">
                  {nicknameAvailable ? (
                    <span className="text-success">✓</span>
                  ) : (
                    <span className="text-danger">✗</span>
                  )}
                </span>
              )}
                </div>
  {errors.nickname && (
    <div className="invalid-feedback d-block">{errors.nickname}</div>
  )}
  {data.nickname && !nicknameAvailable && !errors.nickname && (
    <div className="text-danger small mt-1">This username is already taken</div>
  )}
  <small className="text-muted">Choose a unique username for your portfolio URL</small>
</div>

{/* Location Field */}
<div className="col-md-6">
  <label htmlFor="location" className="form-label">
    <MapPin size={16} className="me-1" />
    Location
  </label>
  <input
    type="text"
    id="location"
    className={`form-control ${errors.location ? 'is-invalid' : ''}`}
    value={data.location || ''}
    onChange={(e) => setData('location', e.target.value)}
    placeholder="e.g., Los Angeles, CA"
  />
  {errors.location && (
    <div className="invalid-feedback d-block">{errors.location}</div>
  )}
  <small className="text-muted">Your city/region for potential meetings and opportunities</small>
</div>

{/* Availability Status */}
<div className="col-md-6">
  <label htmlFor="availability_status" className="form-label">
    <Clock size={16} className="me-1" />
    Current Availability
  </label>
  <select
    id="availability_status"
    className={`form-select ${errors.availability_status ? 'is-invalid' : ''}`}
    value={data.availability_status}
    onChange={(e) => setData('availability_status', e.target.value)}
  >
    {Object.entries(availabilityOptions || {}).map(([value, label]) => (
      <option key={value} value={value}>{label}</option>
    ))}
  </select>
  {errors.availability_status && (
    <div className="invalid-feedback d-block">{errors.availability_status}</div>
  )}
  <small className="text-muted">Let others know your current work status</small>
</div>

{/* Primary Genres */}
<div className="col-12">
  <label className="form-label">
    <Tag size={16} className="me-1" />
    Primary Genres <span className="text-muted">(max 3)</span>
  </label>
  
  <div className="border rounded p-3 mb-2" style={{ minHeight: '60px' }}>
    <div className="d-flex flex-wrap gap-2 mb-2">
      {data.primary_genres.map((genre, index) => (
        <span
          key={index}
          className="badge bg-primary d-flex align-items-center"
          style={{ fontSize: '0.9rem' }}
        >
          {genre}
          <button
            type="button"
            className="btn-close btn-close-white btn-sm ms-2"
            onClick={() => removeGenre(index)}
            style={{ fontSize: '0.6em' }}
          ></button>
        </span>
      ))}
    </div>
    
    {data.primary_genres.length < 3 && (
      <input
        type="text"
        className="form-control form-control-sm border-0 shadow-none"
        placeholder="Add a genre and press Enter..."
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ',') && e.target.value.trim()) {
            e.preventDefault();
            addGenre(e.target.value);
            e.target.value = '';
          }
        }}
        style={{ outline: 'none' }}
      />
    )}
  </div>
  
  {errors.primary_genres && (
    <div className="text-danger small">{errors.primary_genres}</div>
  )}
  <small className="text-muted">
    What genres do you primarily write? Press Enter or comma to add.
  </small>
</div>

{/* Specialty Tagline */}
<div className="col-12">
  <label htmlFor="specialty_tagline" className="form-label">Specialty Tagline</label>
  <input
    type="text"
    id="specialty_tagline"
    className={`form-control ${errors.specialty_tagline ? 'is-invalid' : ''}`}
    value={data.specialty_tagline || ''}
    onChange={(e) => setData('specialty_tagline', e.target.value)}
    placeholder="e.g., Character-driven sci-fi thrillers"
    maxLength="100"
  />
  {errors.specialty_tagline && (
    <div className="invalid-feedback d-block">{errors.specialty_tagline}</div>
  )}
  <small className="text-muted">
    A short phrase describing your writing specialty ({100 - (data.specialty_tagline?.length || 0)} characters remaining)
  </small>
</div>

          {/* Experience Level */}
          <div className="col-md-6">
            <label htmlFor="experienceLevel" className="form-label">Experience Level</label>
            <select
              id="experienceLevel"
              className="form-select"
              value={data.experience_level}
              onChange={e => setData('experience_level', e.target.value)}
            >
              <option value="">Select your experience level</option>
              {Object.entries(experienceLevels).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          
          {/* Writer Type */}
          {/* <div className="col-md-6">
            <label htmlFor="writerType" className="form-label">Writer Type</label>
            <select
              id="writerType"
              className="form-select"
              value={data.writer_type}
              onChange={e => setData('writer_type', e.target.value)}
            >
              <option value="">Select writer type</option>
              {Object.entries(writerTypes).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div> */}

          {/* Bio */}
          <div className="col-12">
            <label htmlFor="bio" className="form-label">Bio</label>
            <textarea
              id="bio"
              className="form-control"
              placeholder="Tell others about yourself as a writer..."
              rows="3"
              value={data.bio}
              onChange={e => setData('bio', e.target.value)}
            ></textarea>
            {errors.bio && (
              <div className="text-danger small mt-1">{errors.bio}</div>
            )}
          </div>
          
  {/* Social Links */}
          <SocialLinksField
            data={data}
            setData={setData}
            socialPlatforms={socialPlatforms}
          />          
        </div>
      </div>
      
      <div className="card-footer text-end">
        <button
          type="submit"
          className="btn btn-primary"
          disabled={processing}
        >
          <Save size={18} className="me-1" /> Save Profile
        </button>
      </div>
    </form>
  );
};

export default UserProfileForm;
